import { t as tr } from '../i18n.js'
import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'
import { api, ApiError, type Budget, type ProjectDetail, type Rate } from '../api.js'
import { atLeast, useAuth } from '../auth.js'
import { ProjectTabs } from './ProjectTabs.js'

const money = (n: number, cur: string | null) => `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur ?? ''}`.trim()

/** Project → Rates: bill rates per member (managers read, admins write) and the money budget. */
export function ProjectRatesPage() {
  const { projectId = '' } = useParams()
  const { user } = useAuth()
  const qc = useQueryClient()
  const canWrite = atLeast(user, 'admin')
  const detail = useQuery({ queryKey: ['project', projectId], queryFn: () => api<ProjectDetail>(`/projects/${projectId}`), retry: false })
  const rates = useQuery({ queryKey: ['bill-rates', projectId], queryFn: () => api<{ rates: Rate[] }>(`/projects/${projectId}/bill-rates`), retry: false })
  const [userId, setUserId] = useState('')
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10))
  const [amount, setAmount] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [err, setErr] = useState<string | null>(null)
  const add = useMutation({
    mutationFn: () => api(`/projects/${projectId}/bill-rates`, { method: 'POST', json: { userId, effectiveFrom: from, amount: Number(amount), currency } }),
    onSuccess: () => { setAmount(''); void qc.invalidateQueries({ queryKey: ['bill-rates', projectId] }); void qc.invalidateQueries({ queryKey: ['budget', projectId] }) },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not add rate'),
  })
  const del = useMutation({ mutationFn: (id: string) => api(`/projects/${projectId}/bill-rates/${id}`, { method: 'DELETE' }), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['bill-rates', projectId] }); void qc.invalidateQueries({ queryKey: ['budget', projectId] }) } })
  if (detail.isError || rates.isError) return <p className="bad">{tr("Rates are visible to managers and administrators.")} <Link to={`/projects/${projectId}`}>{tr("Back")}</Link></p>
  if (!detail.data) return <p className="muted">{tr("Loading…")}</p>
  const p = detail.data.project
  const byUser = new Map<string, Rate[]>()
  for (const r of rates.data?.rates ?? []) byUser.set(r.userId, [...(byUser.get(r.userId) ?? []), r])
  return (
    <div className="boardpage">
      <p className="crumbs"><Link to="/projects">{tr("Projects")}</Link> / {p.code}</p>
      <ProjectTabs projectId={p.id} active="rates" canApprove={detail.data.permissions.manageMembers} canSeeRates />
      <ul className="help small muted">
        <li><strong>{tr("Bill rates")}</strong> (this page) are per person, per project, and effective-dated: the rate in force on an entry's date applies.</li>
        <li><strong>{tr("Cost rates")}</strong> — what a person costs the company per hour, independent of project — are set on <Link to="/people">{tr("People")}</Link>: the <em>{tr("Cost rate")}</em> column shows the current rate; click it to see history or change it (managers see, admins edit).</li>
        <li>Every rate change is audit-logged; rates are never visible to members.</li>
      </ul>
      <BudgetPanel projectId={p.id} canEditHours={detail.data.permissions.update} canEditMoney={canWrite} />
      <section className="card stack">
        <h2>{tr("Bill rates")}</h2>
        {detail.data.members.map((m) => {
          const list = byUser.get(m.userId) ?? []
          const cur = list.find((r) => r.current)
          return (
            <div key={m.userId} className="raterow">
              <div className="row between"><strong>{m.displayName}</strong><span className="mono">{cur ? `${money(cur.amount, cur.currency)}/h` : <span className="muted">{tr("no rate — billable time counts as unrated")}</span>}</span></div>
              {list.length > 0 && <ul className="plain small muted">{list.map((r) => <li key={r.id}>from {r.effectiveFrom}: {money(r.amount, r.currency)}/h{r.current ? ' (current)' : ''} {canWrite && <button className="link danger" onClick={() => del.mutate(r.id)}>remove</button>}</li>)}</ul>}
            </div>
          )
        })}
        {canWrite && (
          <form className="row" onSubmit={(e: FormEvent) => { e.preventDefault(); setErr(null); add.mutate() }}>
            <select value={userId} onChange={(e) => setUserId(e.target.value)} required><option value="">{tr("Person…")}</option>{detail.data.members.map((m) => <option key={m.userId} value={m.userId}>{m.displayName}</option>)}</select>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
            <input type="number" step="0.01" min="0" placeholder={tr("rate / hour")} value={amount} onChange={(e) => setAmount(e.target.value)} required style={{ width: '8rem' }} />
            <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} pattern="[A-Z]{3}" style={{ width: '4.5rem' }} />
            <button className="button">{tr("Add rate")}</button>
          </form>
        )}
        {err && <p className="bad small">{err}</p>}
      </section>
    </div>
  )
}

export function BudgetPanel({ projectId, canEditHours, canEditMoney }: { projectId: string; canEditHours: boolean; canEditMoney: boolean }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['budget', projectId], queryFn: () => api<Budget>(`/projects/${projectId}/budget`) })
  const [edit, setEdit] = useState(false)
  const [hours, setHours] = useState('')
  const [amt, setAmt] = useState('')
  const [cur, setCur] = useState('USD')
  const [pct, setPct] = useState('80')
  const [err, setErr] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => api(`/projects/${projectId}/budget`, { method: 'PUT', json: { budgetHours: hours === '' ? null : Number(hours), ...(canEditMoney ? { budgetAmount: amt === '' ? null : Number(amt), budgetCurrency: amt === '' ? null : cur } : {}), alertPct: Number(pct) } }),
    onSuccess: () => { setEdit(false); void qc.invalidateQueries({ queryKey: ['budget', projectId] }) },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not save'),
  })
  if (!q.data) return null
  const b = q.data
  const startEdit = () => { setHours(b.budgetHours?.toString() ?? ''); setAmt(b.budgetAmount?.toString() ?? ''); setCur(b.budgetCurrency ?? 'USD'); setPct(String(b.alertPct)); setEdit(true) }
  const bar = (p: number | null) => p === null ? null : <div className="progress" style={{ minWidth: '12rem' }}><div className={`bar ${p >= 100 ? 'over' : p >= b.alertPct ? 'warn' : ''}`} style={{ width: `${Math.min(100, p)}%` }} /><span className="small mono">{p}%</span></div>
  return (
    <section className="card stack">
      <div className="row between"><h2 style={{ margin: 0 }}>{tr("Budget")}</h2>{(canEditHours || canEditMoney) && !edit && <button className="link" onClick={startEdit}>{tr("Edit")}</button>}</div>
      {!edit && (
        <div className="grid2">
          <div className="stack">
            <div className="row between"><span>{tr("Hours")}</span><span className="mono">{b.consumedHours}h{b.budgetHours !== null ? ` / ${b.budgetHours}h` : ''}</span></div>
            {bar(b.hoursPct) ?? <span className="muted small">{tr("No hours budget set.")}</span>}
          </div>
          {b.canSeeMoney && (
            <div className="stack">
              <div className="row between"><span>{tr("Billable value")}</span><span className="mono">{money(b.consumedAmount ?? 0, b.budgetCurrency)}{b.budgetAmount !== null ? ` / ${money(b.budgetAmount, b.budgetCurrency)}` : ''}</span></div>
              {bar(b.amountPct) ?? <span className="muted small">{tr("No money budget set.")}</span>}
              <div className="small muted">Cost so far: {money(b.costAmount ?? 0, b.budgetCurrency)}{b.unratedMinutes ? ` · ${Math.round(b.unratedMinutes / 6) / 10}h billable but unrated` : ''}</div>
            </div>
          )}
        </div>
      )}
      {!edit && <p className="small muted">Project administrators are notified once at {b.alertPct}% and again at 100%.</p>}
      {edit && (
        <form className="row" onSubmit={(e) => { e.preventDefault(); setErr(null); save.mutate() }}>
          <label>{tr("Hours")}<input type="number" min="0" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} disabled={!canEditHours} style={{ width: '7rem' }} /></label>
          {canEditMoney && <><label>{tr("Amount")}<input type="number" min="0" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} style={{ width: '9rem' }} /></label>
            <label>{tr("Currency")}<input value={cur} onChange={(e) => setCur(e.target.value.toUpperCase())} maxLength={3} style={{ width: '4.5rem' }} /></label></>}
          <label>{tr("Alert at %")}<input type="number" min="1" max="100" value={pct} onChange={(e) => setPct(e.target.value)} style={{ width: '5rem' }} /></label>
          <button className="button primary" disabled={save.isPending}>{tr("Save")}</button><button type="button" className="button" onClick={() => setEdit(false)}>Cancel</button>
          {err && <span className="bad small">{err}</span>}
        </form>
      )}
    </section>
  )
}
